import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import type { DataTableCellValue, FilterOption } from '../../DataTable.types';
import { SortDirection } from '../../DataTable.types';
import { FilterCheckboxList } from '../../filter-checkbox-list/FilterCheckboxList';
import { i18n } from './ColumnHeaderMenu.i18n';

export interface ColumnHeaderMenuProps {
    anchorEl: HTMLElement | null;
    field: string;
    onClose: () => void;

    // Sort
    sortable: boolean;
    isSortActive: boolean;
    sortDirection: SortDirection;
    sortLabels?: { asc: string; desc: string };
    onSortSet: (field: string, direction: SortDirection) => void;
    onSortClear: (field: string) => void;

    // Filter
    filterOptions?: FilterOption[];
    filterValues: DataTableCellValue[] | undefined;
    onFilterChange: (field: string, values: DataTableCellValue[]) => void;
}

export const ColumnHeaderMenu = ({
    anchorEl,
    field,
    onClose,
    sortable,
    isSortActive,
    sortDirection,
    sortLabels,
    onSortSet,
    onSortClear,
    filterOptions,
    filterValues,
    onFilterChange,
}: ColumnHeaderMenuProps) => {
    const intl = useIntl();
    const open = Boolean(anchorEl);

    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const filterOpen = Boolean(filterAnchorEl);

    const ascLabel = sortLabels?.asc ?? intl.formatMessage(i18n.ascending);
    const descLabel = sortLabels?.desc ?? intl.formatMessage(i18n.descending);

    const hasFilters = filterOptions && filterOptions.length > 0;
    const isFilterActive =
        filterValues !== undefined && filterValues.length !== (filterOptions?.length ?? 0);

    const handleSortAsc = useCallback(() => {
        if (isSortActive && sortDirection === SortDirection.ASC) {
            onSortClear(field);
        } else {
            onSortSet(field, SortDirection.ASC);
        }
        onClose();
    }, [field, isSortActive, sortDirection, onSortSet, onSortClear, onClose]);

    const handleSortDesc = useCallback(() => {
        if (isSortActive && sortDirection === SortDirection.DESC) {
            onSortClear(field);
        } else {
            onSortSet(field, SortDirection.DESC);
        }
        onClose();
    }, [field, isSortActive, sortDirection, onSortSet, onSortClear, onClose]);

    const handleFilterClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(e.currentTarget);
    }, []);

    const handleFilterClose = useCallback(() => {
        setFilterAnchorEl(null);
        onClose();
    }, [onClose]);

    const handleClearFilter = useCallback(() => {
        if (filterOptions) {
            onFilterChange(
                field,
                filterOptions.map((o) => o.value),
            );
        }
        onClose();
    }, [field, filterOptions, onFilterChange, onClose]);

    const handleMenuClose = useCallback(() => {
        setFilterAnchorEl(null);
        onClose();
    }, [onClose]);

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 180 } } }}
                automation-id={`column-menu-${field}`}
            >
                {sortable && [
                    <MenuItem
                        key="sort-asc"
                        onClick={handleSortAsc}
                        automation-id={`column-menu-sort-asc-${field}`}
                    >
                        <ListItemIcon>
                            {isSortActive && sortDirection === SortDirection.ASC ? (
                                <CheckIcon fontSize="small" />
                            ) : (
                                <span />
                            )}
                        </ListItemIcon>
                        <ListItemText>{ascLabel}</ListItemText>
                    </MenuItem>,
                    <MenuItem
                        key="sort-desc"
                        onClick={handleSortDesc}
                        automation-id={`column-menu-sort-desc-${field}`}
                    >
                        <ListItemIcon>
                            {isSortActive && sortDirection === SortDirection.DESC ? (
                                <CheckIcon fontSize="small" />
                            ) : (
                                <span />
                            )}
                        </ListItemIcon>
                        <ListItemText>{descLabel}</ListItemText>
                    </MenuItem>,
                ]}
                {sortable && hasFilters && <Divider key="divider-sort-filter" />}
                {hasFilters && (
                    <MenuItem
                        onClick={handleFilterClick}
                        automation-id={`column-menu-filter-${field}`}
                    >
                        <ListItemIcon>
                            <FilterListIcon
                                fontSize="small"
                                color={isFilterActive ? 'primary' : 'inherit'}
                            />
                        </ListItemIcon>
                        <ListItemText>{intl.formatMessage(i18n.filterBy)}</ListItemText>
                    </MenuItem>
                )}
                {hasFilters && isFilterActive && (
                    <MenuItem
                        onClick={handleClearFilter}
                        automation-id={`column-menu-clear-filter-${field}`}
                    >
                        <ListItemIcon>
                            <ClearIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{intl.formatMessage(i18n.clearFilter)}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
            <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {hasFilters && (
                    <FilterCheckboxList
                        field={field}
                        options={filterOptions}
                        selected={filterValues}
                        onChange={onFilterChange}
                    />
                )}
            </Popover>
        </>
    );
};
